import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Container } from "reactstrap";
import SimpleBar from "simplebar-react";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import { buildSampleCollectionColumns } from "../helper/verification.constants";
import { Timepoint, SubjectRecord } from "../helper/verification.types";
import PinVerificationModal from "modules/Pharmacy/DailyReconsilation/components/PinVerificationModal";
import { useSampleCollectionVerificationService } from "../api/useVerificationQuery";
import { useGlobalStudy } from "context/StudyContext";
import { formatDate, GetUserData, showToastMessage } from "common/service/CommonService";
import { SUBJECT_PATHS, DASHBOARD_PATHS } from "Routes/paths";
import { queryClient } from "api/queryClient";
import styles from "../css/sampleCollectionVerification.module.scss";
import skeletonStyles from "assets/scss/common/viewSkeleton.module.scss";

// ─── Card -Components ───────────────────────────────────────────────────────────

const StatsBar: React.FC<{ data: SubjectRecord[] }> = ({ data }) => {
  const studycode = data[0]?.studycode ?? "-";
  const periodNumber = data[0]?.period_number ?? "-";
  const subjects = data.length;

  return (
    <div className={styles.statsBar}>
      <div className={styles.statItem}>
        <i className="ri-book-open-line" style={{ color: "#6366f1" }} />
        <div>
          <div className={styles.statValue}>{studycode}</div>
          <div className={styles.statLabel}>Study Code</div>
        </div>
      </div>

      <div className={styles.statDivider} />

      <div className={styles.statItem}>
        <i className="ri-calendar-event-line" style={{ color: "#0ea5e9" }} />
        <div>
          <div className={styles.statValue}>Period {periodNumber}</div>
          <div className={styles.statLabel}>Period No.</div>
        </div>
      </div>

      <div className={styles.statDivider} />

      <div className={styles.statItem}>
        <i className="ri-user-line" style={{ color: "#8b5cf6" }} />
        <div>
          <div className={styles.statValue}>{subjects}</div>
          <div className={styles.statLabel}>Total Subjects</div>
        </div>
      </div>
    </div>
  );
};

// ─── Subject-Card -Components ───────────────────────────────────────────────────────────

const SubjectCard: React.FC<{ subject: SubjectRecord; index: number }> = ({ subject, index }) => {
  const [expanded, setExpanded] = useState<boolean>(index === 0);
  const tableColumns = useMemo(() => buildSampleCollectionColumns(), []);

  const sortedTimepoints = useMemo(
    () => [...subject.timepoints].sort((a, b) => a.timepoint_sort - b.timepoint_sort),
    [subject.timepoints]
  );

  return (
    <div className={styles.subjectCard}>
      {/* Expand/Collapse Header */}
      <div
        className={styles.subjectHeader}
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setExpanded((prev) => !prev)}
      >
        <div className={styles.subjectHeaderLeft}>
          <div className={styles.subjectAvatar}>{subject.subjectno}</div>
          <div>
            <div className={styles.subjectTitle}>Subject {subject.subjectno}</div>
          </div>
        </div>
        <i
          className={`ri-arrow-${expanded ? "up" : "down"}-s-line`}
          style={{ fontSize: 18, color: "#94a3b8" }}
        />
      </div>

      {/* Expanded Table Details */}
      {expanded && (
        <div className="px-4 pb-3">
          <CommonTableIndexWise
            columns={tableColumns}
            data={sortedTimepoints}
            hideAction
            paginationShow={false}
          />
        </div>
      )}
    </div>
  );
};


// ─── skeleton Loader ───────────────────────────────────────────────────────────

const SkeletonLoader: React.FC = () => (
  <div className={styles.skeletonWrap} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
    {/* Stats Bar Skeleton */}
    <div className={styles.statsBar}>
      {[1, 2, 3].map((idx) => (
        <React.Fragment key={idx}>
          <div className={styles.statItem} style={{ gap: "10px" }}>
            <div className={`${skeletonStyles.skeleton} ${skeletonStyles.skeletonIcon}`} style={{ width: "24px", height: "24px" }} />
            <div>
              <div className={skeletonStyles.skeleton} style={{ width: "80px", height: "14px", marginBottom: "4px" }} />
              <div className={skeletonStyles.skeleton} style={{ width: "50px", height: "10px" }} />
            </div>
          </div>
          {idx < 3 && <div className={styles.statDivider} />}
        </React.Fragment>
      ))}
    </div>

    {/* Subject Cards Skeleton */}
    {[1, 2].map((idx) => (
      <div key={idx} className={styles.subjectCard}>
        <div className={styles.subjectHeader}>
          <div className={styles.subjectHeaderLeft}>
            <div className={`${skeletonStyles.skeleton} ${styles.subjectAvatar}`} style={{ background: "none" }} />
            <div className={skeletonStyles.skeleton} style={{ width: "100px", height: "14px" }} />
          </div>
          <div className={skeletonStyles.skeleton} style={{ width: "16px", height: "16px" }} />
        </div>
        <div className="px-4 pb-3 pt-3">
          <div className="d-flex gap-3 mb-2 pb-2 border-bottom" style={{ borderColor: "#f1f5f9" }}>
            <div className={skeletonStyles.skeleton} style={{ width: "40px", height: "12px", flexShrink: 0 }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={skeletonStyles.skeleton} style={{ flex: 1, height: "12px" }} />
            ))}
          </div>
          {[1, 2].map((rowIdx) => (
            <div key={rowIdx} className="d-flex gap-3 py-2 border-bottom" style={{ borderColor: "#f1f5f9" }}>
              <div className={skeletonStyles.skeleton} style={{ width: "40px", height: "10px", flexShrink: 0 }} />
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={skeletonStyles.skeleton} style={{ flex: 1, height: "10px" }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const SampleCollectionVerification: React.FC = () => {
  document.title = "Sample Collection Verification | ZENTIXS VISTA";

  const navigate = useNavigate();
  const location = useLocation();

  // ─── State Hook Managers ────────────────────────────────────────────────────
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [showWarningDetails, setShowWarningDetails] = useState<boolean>(false);
  
  // ─── User Context Data ──────────────────────────────────────────────────────
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const periodid = location.state?.periodid as number | undefined;
  const studyid = Number(globalStudy?.studyid);
  const userData = GetUserData();

  // ─── API Hook ────────────────────────────────────────────────
  const { SampleCollectionQuery, GlobalVerifyMutation } = useSampleCollectionVerificationService({
    studyid,
    periodid: periodid ?? 0,
    enabled: !!studyid && !!periodid,
  });

  const data: SubjectRecord[] = useMemo(
    () => SampleCollectionQuery?.data?.data ?? [],
    [SampleCollectionQuery?.data]
  );

  // ─── Derived Validation & Error Mappings ───────────────────────────────────
  
  const validation = useMemo(() => {
    const unverifiedMap: Record<string, { subjectno: string; volunteerid?: number }> = {};
    const missingMap: Record<string, { subjectno: string; volunteerid?: number }> = {};

    data.forEach((subject) => {
      subject?.timepoints?.forEach((tp) => {
        const volunteerid = subject.volunteerid;

        // 1. Check for missing collection times
        if (!tp.samplecollectiontime || tp.samplecollectiontime.trim() === "") {
          if (!missingMap[subject.subjectno]) {
            missingMap[subject.subjectno] = {
              subjectno: subject.subjectno,
              volunteerid,
            };
          }
        }

        // 2. Check for unverified corrections
        if (tp.iscorrection && !tp.iscorrectionverified) {
          if (!unverifiedMap[subject.subjectno]) {
            unverifiedMap[subject.subjectno] = {
              subjectno: subject.subjectno,
              volunteerid,
            };
          }
        }
      });
    });

    const unverifiedCorrections = Object.values(unverifiedMap);
    const missingCollectionTimes = Object.values(missingMap);

    const hasMissingCollectionTime = missingCollectionTimes.length > 0;
    const hasUnverifiedCorrection = unverifiedCorrections.length > 0;
    const isValid = !hasMissingCollectionTime && !hasUnverifiedCorrection;

    return {
      isValid,
      hasMissingCollectionTime,
      hasUnverifiedCorrection,
      unverifiedCorrections,
      missingCollectionTimes,
    };
  }, [data]);

  // ─── Action Handlers ────────────────────────────────────────────────────────

  const onVerifyPinSubmit = (password: string) => {
    const payload = {
      studyid,
      periodid: periodid ?? 0,
      verifiedby: Number(userData?.userid),
      verifypin: password,
    };

    GlobalVerifyMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({
          queryKey: ["sample-collection-verification", studyid, periodid],
        });
        queryClient.invalidateQueries({
          queryKey: ["verification-notification-count", studyid],
        });
        showToastMessage("success", response?.message || "Verification successful");
        setVerifyModal(false);
        navigate(DASHBOARD_PATHS.DEFAULT);
      },
      onError: (error: any) => {
        showToastMessage(
          "error",
          error?.response?.data?.message || "Verification failed"
        );
      },
    });
  };

  const handleSubjectNavigate = (subjectno: string, volunteerid?: number) => {
    sessionStorage.setItem("subjectno", String(subjectno));
    if (volunteerid) {
      sessionStorage.setItem("activevolunteerid", String(volunteerid));
    }
    navigate("/study/detail/subject/subjectinfo/periods");
  };

  return (
    <div className="page-content">
      <Container fluid className="pb-4">
        {/* Header & Primary Actions */}
        <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
          <div className="d-flex align-items-center gap-2">
            <button className={styles.backBtn} onClick={() => navigate(DASHBOARD_PATHS.DEFAULT)} type="button">
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <h5 className="mb-0 fw-bold text-dark">Sample Collection Verification</h5>
              <p className="text-muted fs-13 mb-0">
                Review and verify sample collection timepoints for all subjects in this period.
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Button
              color="success"
              outline
              className="btn-sm fw-semibold"
              disabled={!validation.isValid}
              onClick={() => setVerifyModal(true)}
            >
              <i className="ri-checkbox-circle-line me-1" /> Verify
            </Button>
            <Button color="danger" outline className="btn-sm fw-semibold">
              <i className="ri-close-circle-line me-1" /> Reject
            </Button>
          </div>
        </div>

        {/* Validation Errors & Alert Details */}
        {!SampleCollectionQuery.isFetching && data.length > 0 && !validation.isValid && (
          <div className={styles.warningContainer}>
            <div className={styles.warningAlert}>
              <div className={styles.warningAlertLeft}>
                <i className="ri-error-warning-line fs-18 text-warning" />
                <div className="d-flex flex-column gap-1 align-items-start">
                  <span className={styles.warningAlertText}>
                    Verification is disabled because one or more subject timepoints have missing collection times or unverified corrections. Please resolve these issues on the Subject Info Periods page.
                  </span>
                  {(validation.unverifiedCorrections.length > 0 || validation.missingCollectionTimes.length > 0) && (
                    <button
                      className={styles.detailsToggleBtn}
                      onClick={() => setShowWarningDetails(!showWarningDetails)}
                      type="button"
                    >
                      <span>{showWarningDetails ? "Hide Details" : "Show Details"}</span>
                      <i className={`ri-arrow-${showWarningDetails ? "up" : "down"}-s-line`} style={{ fontSize: "16px", verticalAlign: "middle" }} />
                    </button>
                  )}
                </div>
              </div>
              <Button
                color="warning"
                outline
                className="btn-sm fw-semibold"
                onClick={() => navigate('/study/detail/subject')}
                style={{ fontSize: "12px", padding: "6px 16px", whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Go to Subject Periods
              </Button>
            </div>

            {/* Collapsible Details Grid */}
            {showWarningDetails && (
              <div className={styles.warningDetails}>
                {/* Unverified Corrections section */}
                {validation.unverifiedCorrections.length > 0 && (
                  <div className={styles.warningDetailsSection}>
                    <div className={styles.warningDetailsTitle}>
                      <i className="ri-error-warning-fill text-danger me-2" style={{ fontSize: "16px" }} />
                      Unverified Corrections ({validation.unverifiedCorrections.length})
                    </div>
                    <div className={styles.warningDetailsGrid}>
                      {validation.unverifiedCorrections.map((item, idx) => (
                        <div
                          key={idx}
                          className={`${styles.warningDetailCard} ${styles.clickableCard}`}
                          onClick={() => handleSubjectNavigate(item.subjectno, item.volunteerid)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && handleSubjectNavigate(item.subjectno, item.volunteerid)}
                          title={`Go to Subject ${item.subjectno} Periods`}
                        >
                          <div className={styles.warningDetailCardLeft}>
                            <div className={`${styles.avatarMini} ${styles.avatarCorrection}`}>
                              {item.subjectno}
                            </div>
                            <div>
                              <div className={styles.warningDetailSubject}>Subject {item.subjectno}</div>
                            </div>
                          </div>
                          <i className="ri-arrow-right-line text-muted" style={{ fontSize: "14px" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missing Collection Times section */}
                {validation.missingCollectionTimes.length > 0 && (
                  <div className={`${styles.warningDetailsSection} ${validation.unverifiedCorrections.length > 0 ? "mt-4" : ""}`}>
                    <div className={styles.warningDetailsTitle}>
                      <i className="ri-time-fill text-warning me-2" style={{ fontSize: "16px" }} />
                      Missing Collection Times ({validation.missingCollectionTimes.length})
                    </div>
                    <div className={styles.warningDetailsGrid}>
                      {validation.missingCollectionTimes.map((item, idx) => (
                        <div
                          key={idx}
                          className={`${styles.warningDetailCard} ${styles.clickableCard}`}
                          onClick={() => handleSubjectNavigate(item.subjectno, item.volunteerid)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && handleSubjectNavigate(item.subjectno, item.volunteerid)}
                          title={`Go to Subject ${item.subjectno} Periods`}
                        >
                          <div className={styles.warningDetailCardLeft}>
                            <div className={`${styles.avatarMini} ${styles.avatarMissing}`}>
                              {item.subjectno}
                            </div>
                            <div>
                              <div className={styles.warningDetailSubject}>Subject {item.subjectno}</div>
                            </div>
                          </div>
                          <i className="ri-arrow-right-line text-muted" style={{ fontSize: "14px" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Main Panel Contents ── */}
        {SampleCollectionQuery.isFetching ? (
          <SkeletonLoader />
        ) : !periodid ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <i className="ri-error-warning-line" />
            </div>
            <h3 className={styles.emptyTitle}>No Period Selected</h3>
            <p className={styles.emptyText}>
              Please navigate here from the Verification Queue with a valid period ID.
            </p>
            <Button color="primary" outline onClick={() => navigate(-1)}>
              <i className="ri-arrow-left-line me-1" /> Go Back
            </Button>
          </div>
        ) : SampleCollectionQuery.isError ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon} style={{ color: "#ef4444" }}>
              <i className="ri-close-circle-line" />
            </div>
            <h3 className={styles.emptyTitle}>Failed to Load Data</h3>
            <p className={styles.emptyText}>
              There was an error fetching sample collection data. Please try again.
            </p>
            <Button color="primary" outline onClick={() => SampleCollectionQuery.refetch()}>
              <i className="ri-refresh-line me-1" /> Retry
            </Button>
          </div>
        ) : data.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <i className="ri-inbox-line" />
            </div>
            <h3 className={styles.emptyTitle}>No Records Found</h3>
            <p className={styles.emptyText}>
              No sample collection data found for the selected period.
            </p>
          </div>
        ) : (
          <>
            {/* Header Stats Summary */}
            <StatsBar data={data} />

            {/* Scrollable Subject Accordion Cards */}
            <SimpleBar style={{ maxHeight: "calc(100vh - 230px)" }}>
              <div className={styles.subjectList}>
                {data
                  .slice()
                  .sort((a, b) => a.subjectno.localeCompare(b.subjectno))
                  .map((subject, idx) => (
                    <SubjectCard
                      key={`${subject.subjectno}-${subject.period_number}`}
                      subject={subject}
                      index={idx}
                    />
                  ))}
              </div>
            </SimpleBar>
          </>
        )}
      </Container>

      {/* Global verification pin dialog */}
      <PinVerificationModal
        isOpen={verifyModal}
        toggle={() => setVerifyModal(false)}
        onVerify={onVerifyPinSubmit}
        isLoading={GlobalVerifyMutation.isPending}
        title="Verify Sample Collection"
        description={
          <>
            Please enter your password to globally verify Sample Collection for this period.
          </>
        }
        isInvalid={GlobalVerifyMutation.isError}
      />
    </div>
  );
};

export default SampleCollectionVerification;
